import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Briefcase, Loader2 } from 'lucide-react';
import { useShopHiringSettings, useUpdateShopHiring } from '@/hooks/useConnectApi';

const HIRING_ROLE_OPTIONS = [
  { value: 'technician', label: 'Technician' },
  { value: 'advisor', label: 'Advisor / Service Writer' },
  { value: 'manager', label: 'Manager' },
  { value: 'student', label: 'Student / Intern' },
] as const;

interface FormValues {
  recruiting_enabled: boolean;
  hiring_roles: string[];
  hiring_blurb: string;
}

export function ShopHiringControls() {
  const { data: settings, isLoading } = useShopHiringSettings();
  const updateMutation = useUpdateShopHiring();

  const form = useForm<FormValues>({
    defaultValues: {
      recruiting_enabled: false,
      hiring_roles: [],
      hiring_blurb: '',
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset({
        recruiting_enabled: settings.recruiting_enabled ?? false,
        hiring_roles: settings.hiring_roles ?? [],
        hiring_blurb: settings.hiring_blurb ?? '',
      });
    }
  }, [settings]);

  const onSubmit = (values: FormValues) => {
    updateMutation.mutate({
      recruiting_enabled: values.recruiting_enabled,
      hiring_roles: values.hiring_roles,
      hiring_blurb: values.hiring_blurb || null,
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const isHiring = form.watch('recruiting_enabled');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Hiring Settings</CardTitle>
        </div>
        <CardDescription>
          Control whether your shop appears to job seekers and which roles you're hiring for.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {/* Actively Hiring toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="recruiting_enabled">Actively Hiring</Label>
            <Switch
              id="recruiting_enabled"
              checked={form.watch('recruiting_enabled')}
              onCheckedChange={(v) => form.setValue('recruiting_enabled', v)}
            />
          </div>

          {isHiring && (
            <>
              {/* Hiring Roles */}
              <div className="space-y-2">
                <Label>Hiring Roles</Label>
                {HIRING_ROLE_OPTIONS.map((role) => {
                  const roles = form.watch('hiring_roles');
                  return (
                    <div key={role.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`role-${role.value}`}
                        checked={roles.includes(role.value)}
                        onCheckedChange={(checked) => {
                          const current = form.getValues('hiring_roles');
                          form.setValue(
                            'hiring_roles',
                            checked
                              ? [...current, role.value]
                              : current.filter((r) => r !== role.value)
                          );
                        }}
                      />
                      <Label htmlFor={`role-${role.value}`} className="font-normal cursor-pointer">
                        {role.label}
                      </Label>
                    </div>
                  );
                })}
              </div>

              {/* Hiring Blurb */}
              <div className="space-y-1.5">
                <Label>Hiring Description (optional)</Label>
                <Textarea
                  placeholder="Tell candidates about your shop and what you're looking for..."
                  maxLength={500}
                  {...form.register('hiring_blurb')}
                />
                <p className="text-xs text-muted-foreground">Visible to candidates browsing your shop.</p>
              </div>
            </>
          )}

          <Button type="submit" disabled={updateMutation.isPending} className="w-full">
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Hiring Settings
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
